import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import type { ReactElement, RefObject, ReactNode, Ref } from 'react';
import { useRef } from 'react';
import type {
  ModernCreativityInfoItemProps,
  ModernCreativityInfoItemRef,
} from './ModernCreativityInfoItem.data';
import styles from './ModernCreativityInfoItem.module.scss';
import {
  setupTransitionInTimeline,
  setupTransitionOutTimeline,
} from './ModernCreativityInfoItem.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import {
  useMountTransition,
  useTransitionController,
  useUnMountTransition,
} from 'util/react-transition-component';
import PrimaryButton from '../../../atoms/primary-button/PrimaryButton';
import { NODE_PARAGRAPH, render } from 'storyblok-rich-text-react-renderer';
import Heading from '../../../atoms/heading/Heading';
import type { HeadingRef } from '../../../atoms/heading/Heading.data';
import Text from '../../../atoms/text/Text';
import type { TextRef } from '../../../atoms/text/Text.data';
import type { PrimaryButtonRef } from '../../../atoms/primary-button/PrimaryButton.data';
import { isTouch } from '../../../../hooks/useIsDesktopDevice';
import { dataSourceToUrlQuery } from '../../../../util/url.utils';

export default function ModernCreativityInfoItem({
  isVisible,
  transitionOnMount = false,
  transitionOnUnMount = false,
  className,
  blok,
  link,
  category,
  ref,
}: ModernCreativityInfoItemProps & { ref?: Ref<ModernCreativityInfoItemRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const titleRef = useRef<HeadingRef>(null);
  const categoryRef = useRef<HeadingRef>(null);
  const descriptionRef = useRef<TextRef>(null);
  const linkRef = useRef<PrimaryButtonRef>(null);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        titleRef,
        descriptionRef,
        linkRef,
        categoryRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [],
  );

  useChangeTransitionState(transitionController, isVisible);
  useMountTransition(transitionController, transitionOnMount);
  useUnMountTransition(transitionController, transitionOnUnMount);
  return (
    <div
      ref={elementRef}
      className={classNames(styles.modernCreativityInfoItem, className)}
      {...sbEditable(blok)}
    >
      <Heading
        ref={categoryRef}
        as="h2"
        size="label-large"
        className={classNames(styles.category)}
        disableTriggerInOnScroll
        transitionOutType={isTouch ? 'splitWordsSlideOutUp' : 'splitCharsSlideOutUp'}
        transitionInType={isTouch ? 'splitWordsSlideInUp' : 'splitCharsSlideInUp'}
      >
        {category}
      </Heading>

      <Heading
        ref={titleRef}
        as="h3"
        size="heading-03"
        className={classNames(styles.title)}
        disableTriggerInOnScroll
        transitionOutType={isTouch ? 'splitWordsSlideOutUp' : 'splitCharsSlideOutUp'}
        transitionInType={isTouch ? 'splitWordsSlideInUp' : 'splitCharsSlideInUp'}
      >
        {render(blok.title, {
          nodeResolvers: {
            [NODE_PARAGRAPH]: (children: ReactNode) => (
              <>
                {children}
                <br />
              </>
            ),
          },
        })}
      </Heading>

      <Text
        ref={descriptionRef}
        transitionInType="splitWordsSlideInUp"
        transitionOutType="splitWordsSlideOutUp"
        className={classNames(styles.description)}
        as="p"
        size="body-large"
        disableTriggerInOnScroll
      >
        {blok.description}
      </Text>

      <PrimaryButton
        ref={linkRef}
        className={classNames(styles.button)}
        link={{
          href: `${link?.link?.cached_url}${
            blok?.filter ? `?category=${dataSourceToUrlQuery(blok.filter)}` : ''
          }`,
        }}
        label={link.title}
        disableTriggerInOnScroll
      />
    </div>
  );
}
